import React from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image
} from "@react-pdf/renderer";
import dayjs from "dayjs";
import BrianyCareLogo from "./logo.png";

/* ---------------- PAGINATION SETTINGS ---------------- */

const FIRST_PAGE_ROWS = 14;
const OTHER_PAGE_ROWS = 18;

const chunk = (arr, size) => {
  const res = [];
  for (let i = 0; i < arr.length; i += size) {
    res.push(arr.slice(i, i + size));
  }
  return res;
};

/* ---------------- HELPERS ---------------- */

const formatDate = (d) => {
  if (!d) return "N/A";
  return dayjs(d).format("MMM DD YYYY");
};

const buildWeeks = (start, end) => {
  if (!start || !end) return [];
  const weeks = [];
  let current = dayjs(start).startOf("week");
  const last = dayjs(end);

  while (current.isBefore(last) || current.isSame(last)) {
    const weekStart = current;
    const weekEnd = current.add(6, "day");

    weeks.push({
      start: weekStart,
      end: weekEnd,
      label: `${weekStart.format("MMM DD")} - ${weekEnd.format("MMM DD")}`
    });

    current = current.add(7, "day");
  }

  return weeks;
};

const timeToSeconds = (t) => {
  if (!t) return 0;
  const [h = 0, m = 0, s = 0] = t.split(":").map(Number);
  return h * 3600 + m * 60 + s;
};

const secondsToHM = (sec) => {
  const total = Number(sec || 0);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  return `${h}:${String(m).padStart(2, "0")}`;
};

/* ---------------- STYLES ---------------- */

const styles = StyleSheet.create({
  page: { padding: 30, fontSize: 9, fontFamily: "Helvetica" },

  headerContainer: {
    alignItems: "center",
    marginBottom: 25,
    borderBottom: "1 solid #000",
    paddingBottom: 15
  },

  logoRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 8
  },

  logo: { width: 180, height: 35 },

  mainHeading: { fontSize: 14, fontWeight: "bold", marginTop: 6 },
  reportText: { fontSize: 11, marginTop: 4 },
  weekTitle: { fontSize: 11, fontWeight: "bold", marginBottom: 6, marginTop: 12 },

  table: { width: "100%", border: "1 solid #cbd5e1", marginBottom: 20 },

  tableHeader: { flexDirection: "row", backgroundColor: "#2563eb" },
  tableRow: { flexDirection: "row", borderTop: "1 solid #e2e8f0" },
  tableRowAlt: {
    flexDirection: "row",
    borderTop: "1 solid #e2e8f0",
    backgroundColor: "#f8fafc"
  },

  totalRow: {
    flexDirection: "row",
    borderTop: "2 solid #000",
    backgroundColor: "#e2e8f0"
  },

  colName: { width: "28%", padding: 5, borderRight: "1 solid #e2e8f0" },
  colDay: { width: "8%", padding: 4, borderRight: "1 solid #e2e8f0", textAlign: "center" },
  colTotal: { width: "12%", padding: 5, textAlign: "center" },

  headerText: { color: "#fff", fontSize: 8, fontWeight: "bold" },
  cellBold: { fontSize: 8, fontWeight: "bold" },
  center: { textAlign: "center", fontSize: 8 },

  nameContainer: { flexDirection: "row", alignItems: "center" },

  empIdBadge: {
    marginLeft: 6,
    paddingHorizontal: 6,
    paddingVertical: 2,
    backgroundColor: "#e2e8f0",
    borderRadius: 10
  },

  empIdText: { fontSize: 7, fontWeight: "bold" }
});

/* ---------------- COMPONENT ---------------- */

const EmployeeReportPDF = ({ employees = [], dateRange }) => {
  const weekDays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat",'Sun']; // ← added Saturday
  const weeks = buildWeeks(dateRange?.startDate, dateRange?.endDate);

  return (
    <Document>
      <Page size="A4" orientation="landscape" style={styles.page}>
        {/* HEADER */}
        <View style={styles.headerContainer}>
          <View style={styles.logoRow}>
            <Image src={BrianyCareLogo} style={styles.logo} />
          </View>

          <Text style={styles.mainHeading}>WEEKLY EMPLOYEE RECAP</Text>
          <Text style={styles.reportText}>
            Report - {formatDate(dateRange?.startDate)} to {formatDate(dateRange?.endDate)}
          </Text>
        </View>

        {weeks.map((week, wIndex) => {
          const rows = [];

          employees.forEach(emp => {
            const weekMap = { Mon:"0:00:00", Tue:"0:00:00", Wed:"0:00:00", Thu:"0:00:00", Fri:"0:00:00", Sat:"0:00:00",
  Sun:"0:00:00" };

            emp.recap?.forEach(r => {
              const d = dayjs(r.date);
              if (d.isAfter(week.start.subtract(1,"day")) &&
                  d.isBefore(week.end.add(1,"day"))) {
                const day = d.format("ddd");
                if (weekMap[day] !== undefined) {
                  weekMap[day] = r.total_hours || "0:00:00";
                }
              }
            });

            const totalSec = Object.values(weekMap).reduce((a,b)=>a+timeToSeconds(b),0);
            if (totalSec === 0) return;

            rows.push({ emp, weekMap, totalSec });
          });

          if (rows.length === 0) return null;

          let pages = [];

          if (wIndex === 0) {
            pages.push(rows.slice(0, FIRST_PAGE_ROWS));
            const remaining = rows.slice(FIRST_PAGE_ROWS);
            pages.push(...chunk(remaining, OTHER_PAGE_ROWS));
          } else {
            pages = chunk(rows, OTHER_PAGE_ROWS);
          }

          const totals = {
            totalSec: 0,
            days: { Mon:0, Tue:0, Wed:0, Thu:0, Fri:0, Sat:0 ,Sun:0 }
          };

          rows.forEach(r=>{
            totals.totalSec += r.totalSec;
            weekDays.forEach(d=>{
              totals.days[d] += timeToSeconds(r.weekMap[d]);
            });
          });

          return pages.map((pageRows, pageIndex) => (
            <View key={`${wIndex}-${pageIndex}`} break={!(wIndex === 0 && pageIndex === 0)}>
              <Text style={styles.weekTitle}>Week: {week.label}</Text>

              <View style={styles.table}>
                <View style={styles.tableHeader}>
                  <View style={styles.colName}>
                    <Text style={styles.headerText}>Employee</Text>
                  </View>

                  {weekDays.map((d,i)=>(
                    <View key={i} style={styles.colDay}>
                      <Text style={styles.headerText}>{d}</Text>
                    </View>
                  ))}

                  <View style={styles.colTotal}>
                    <Text style={styles.headerText}>Total</Text>
                  </View>
                </View>

                {pageRows.map((r, index)=>(
                  <View key={index} style={index%2?styles.tableRowAlt:styles.tableRow}>
                    <View style={styles.colName}>
                      <View style={styles.nameContainer}>
                        <Text style={styles.cellBold}>{r.emp.name}</Text>
                        <View style={styles.empIdBadge}>
                          <Text style={styles.empIdText}>{r.emp.id}</Text>
                        </View>
                      </View>
                    </View>

                    {weekDays.map((d,i)=>(
                      <View key={i} style={styles.colDay}>
                        <Text style={styles.center}>
                          {secondsToHM(timeToSeconds(r.weekMap[d]))}
                        </Text>
                      </View>
                    ))}

                    <View style={styles.colTotal}>
                      <Text style={styles.center}>{secondsToHM(r.totalSec)}</Text>
                    </View>
                  </View>
                ))}

                {pageIndex === pages.length - 1 && (
                  <View style={styles.totalRow}>
                    <View style={styles.colName}>
                      <Text style={styles.cellBold}>TOTAL</Text>
                    </View>

                    {weekDays.map((d,i)=>(
                      <View key={i} style={styles.colDay}>
                        <Text style={styles.cellBold}>
                          {secondsToHM(totals.days[d])}
                        </Text>
                      </View>
                    ))}

                    <View style={styles.colTotal}>
                      <Text style={styles.cellBold}>
                        {secondsToHM(totals.totalSec)}
                      </Text>
                    </View>
                  </View>
                )}
              </View>
            </View>
          ));
        })}
      </Page>
    </Document>
  );
};

export default EmployeeReportPDF;